import { Box, Group, Paper, Progress, Stack, Text } from '@mantine/core';
import { IconInfoCircle } from '@tabler/icons-react';
import dayjs from 'dayjs';
import { ActionButton } from '../common/ActionButton';
import { AppTooltip } from '../common/AppTooltip';
import {
  GivebackRiskIndicator,
  isSubawardeeAtGivebackRisk,
} from '../financials/GivebackRiskCard';
import { ComponentProjectFinancialsItem, FinancialAsOfPeriod } from '../../types/financials';
import { formatTimestampToLocal } from '../../utils/dateUtils';

const formatCurrency = (n: number): string =>
  new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  }).format(n);

function formatAsOf(period: FinancialAsOfPeriod | null): string | null {
  if (!period) return null;
  return dayjs(`${period.year}-${String(period.month).padStart(2, '0')}-01`).format('MMMM YYYY');
}

function pctOfTarget(actual: number, target: number): number {
  if (target <= 0) return 0;
  return Math.round((actual / target) * 100);
}

function SpendBar({
  label,
  value,
  target,
  asOf,
  color = 'blue',
  pctSuffix,
}: {
  label: string;
  value: number | null;
  target?: number | null;
  asOf: string | null;
  /** Identity color — matches admin Subawardee match (cyan) / Federal (blue). */
  color?: string;
  /** e.g. "spend" → "7% of spend"; "match" → "31% of match". */
  pctSuffix?: 'spend' | 'match';
}) {
  const hasValue = value !== null;
  const hasTarget = typeof target === 'number' && Number.isFinite(target);
  const pct = hasValue && hasTarget ? pctOfTarget(value, target) : null;
  const barPct = pct === null ? 0 : Math.min(pct, 100);
  const overTarget = hasValue && hasTarget && value > target;

  return (
    <Stack spacing={6}>
      <Group position="apart" noWrap>
        <Text size="sm" weight={600}>
          {label}
        </Text>
        <Text size="sm" weight={700} sx={{ fontVariantNumeric: 'tabular-nums' }}>
          {hasValue ? formatCurrency(value) : '—'}
          {hasTarget ? (
            <Text span size="xs" color="dimmed" weight={500}>
              {' '}
              / {formatCurrency(target)}
            </Text>
          ) : null}
        </Text>
      </Group>
      {hasTarget ? (
        <Progress
          value={hasValue ? barPct : 0}
          size="md"
          radius="xl"
          color={overTarget ? 'orange' : pct !== null && pct >= 100 ? 'green' : color}
          aria-label={`${label}: ${pct ?? 0}% of ${pctSuffix ?? 'target'}`}
        />
      ) : null}
      <Group
        position="apart"
        noWrap
        spacing="xs"
        align="flex-start"
        sx={{ minHeight: 18 }}
      >
        <Text size="xs" color="dimmed" sx={{ lineHeight: '18px' }}>
          {asOf ? `As of ${asOf}` : '\u00a0'}
        </Text>
        {hasTarget && pct !== null && pctSuffix ? (
          <Text
            size="xs"
            color="dimmed"
            ta="right"
            sx={{ fontVariantNumeric: 'tabular-nums', lineHeight: '18px' }}
          >
            {`${pct}% of ${pctSuffix}`}
          </Text>
        ) : null}
      </Group>
    </Stack>
  );
}

export type SubawardeeFinancialsCardProps = {
  item: ComponentProjectFinancialsItem;
  title: string;
  onOpen: (subawardeeId: string) => void;
};

/**
 * Summary card for one subawardee's cumulative federal/match spend and earned revenue.
 */
export function SubawardeeFinancialsCard({
  item,
  title,
  onOpen,
}: Readonly<SubawardeeFinancialsCardProps>) {
  const lastUpdated =
    item.lastUpdatedAt != null
      ? formatTimestampToLocal(item.lastUpdatedAt, 'MMM D, YYYY [at] h:mm A')
      : null;

  const updatedTooltip = lastUpdated
    ? [
        `Updated ${lastUpdated}`,
        item.lastUpdatedBy ? `by ${item.lastUpdatedBy}` : null,
      ]
        .filter(Boolean)
        .join(' ')
    : 'No spend reported yet';

  const atGivebackRisk = isSubawardeeAtGivebackRisk(item);

  return (
    <Paper
      shadow="md"
      p="md"
      radius="md"
      role="article"
      aria-label={atGivebackRisk ? `${title}, giveback risk` : title}
      sx={(theme) => ({
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        border: theme.colorScheme === 'dark' ? `1px solid ${theme.colors.dark[4]}` : 'none',
        boxShadow:
          theme.colorScheme === 'dark'
            ? '0 0 12px rgba(255, 255, 255, 0.1)'
            : theme.shadows.md,
        '@media (max-width: 768px)': {
          padding: theme.spacing.sm,
        },
      })}
    >
      <Stack spacing="md" sx={{ flex: 1 }}>
        <Group spacing={6} noWrap align="center">
          <Text
            weight={600}
            size="lg"
            sx={(theme) => ({
              '@media (max-width: 768px)': {
                fontSize: theme.fontSizes.md,
                lineHeight: 1.3,
              },
            })}
          >
            {title}
          </Text>
          <AppTooltip label={updatedTooltip} openDelay={100} withinPortal inline>
            <Box
              component="span"
              sx={(theme) => ({
                display: 'inline-flex',
                color: theme.colorScheme === 'dark' ? theme.colors.dark[2] : theme.colors.gray[6],
                flexShrink: 0,
              })}
              aria-label={updatedTooltip}
            >
              <IconInfoCircle size={16} stroke={1.75} />
            </Box>
          </AppTooltip>
        </Group>

        <Stack spacing="md">
          <SpendBar
            label="Federal Spend"
            value={item.federalSpendToDate}
            target={item.spendTargets.federalTarget}
            asOf={formatAsOf(item.federalAsOf)}
            color="blue"
            pctSuffix="spend"
          />
          <SpendBar
            label="Match Spend"
            value={item.matchSpendToDate}
            target={item.spendTargets.matchTarget}
            asOf={formatAsOf(item.matchAsOf)}
            color="cyan"
            pctSuffix="match"
          />
          <SpendBar
            label="Earned Revenue"
            value={item.earnedRevenueToDate}
            asOf={formatAsOf(item.earnedRevenueAsOf)}
            color="teal"
          />
        </Stack>
      </Stack>

      <Group
        mt="md"
        position="apart"
        align="center"
        noWrap
        sx={{
          '@media (max-width: 480px)': {
            flexDirection: 'column',
            alignItems: 'stretch',
            gap: 12,
          },
        }}
      >
        <GivebackRiskIndicator value={item.givebackRisk} reserveSpace />
        <ActionButton
          onClick={() => onOpen(item.subawardeeId)}
          label="View"
          ariaLabel={`View financials for ${title}`}
          actionType="primary"
          width={100}
        />
      </Group>
    </Paper>
  );
}
